import { z } from 'zod';
import { OrderDirection } from '@/constants/orderDirection';
import { STARTED_TARGETS_FILTER_OPTIONS } from '@/features/operation-package/-components/SelectTargetFlow/-components/person/StartedTargetsFilter/types';

/**
 * アラートカテゴリ（API用・具体化された形式）
 *
 * フォーマット: {operationType}-{status}
 *
 * Provisioning（一括発行）:
 * - provisioning-required: 要発行者（アカウント未発行の従業員）
 * - provisioning-scheduled: 発行予定者（入社予定日が算出日以降。算出日は常に今日）
 *
 * Deprovisioning（一括停止/削除）:
 * - deprovisioning-violated: 要停止者（退社済みだがアカウント残存）— ITF関連
 * - deprovisioning-scheduled: 停止予定者（退社予定日が算出日以降。算出日は常に今日）— ITF関連
 * - deprovisioning-alumni: 離籍者（既に離籍している従業員）— HRF関連
 */
export type AlertCategory =
// Provisioning
'provisioning-required' | 'provisioning-scheduled'
// Deprovisioning
| 'deprovisioning-violated' | 'deprovisioning-scheduled' | 'deprovisioning-alumni'
// 組織改編による異動予定者（一括発行/停止の全パターン共通）
| 'restructuring';

/**
 * アラートカテゴリの配列（バリデーション用）
 */
const ALERT_CATEGORIES = [
// Provisioning
'provisioning-required', 'provisioning-scheduled',
// Deprovisioning
'deprovisioning-violated', 'deprovisioning-scheduled', 'deprovisioning-alumni',
// 組織改編
'restructuring'] as const;

/**
 * 一括発行向けのアラートカテゴリ（全カテゴリ）。
 * 即時パターンでも組織改編対象者（applyPlanDate <= 今日）を
 * 救済発行できるよう「異動予定者」を含める。
 * ただし「発行予定者」（入社予定日が未来）は即時発行の対象外のため、
 * 即時パターンでは利用側（BulkAppUserTargetSelection）で除外する。
 */
export const PROVISIONING_ALERT_CATEGORIES = ['provisioning-required', 'provisioning-scheduled', 'restructuring'] as const;

/**
 * 一括停止向けのアラートカテゴリ（全カテゴリ）。
 * 即時パターンでも組織改編対象者（applyPlanDate <= 今日）を
 * 救済停止できるよう「異動予定者」を含める。
 * ただし「停止予定者」（退社予定日が未来）は即時停止の対象外のため、
 * 即時パターンでは利用側（BulkAppUserTargetSelection）で除外する。
 */
export const DEPROVISIONING_ALERT_CATEGORIES = ['deprovisioning-violated', 'deprovisioning-scheduled', 'deprovisioning-alumni', 'restructuring'] as const;

/**
 * IT Operation Core 対象者選択の URL 検索パラメータスキーマ
 */
export const itOperationCoreTargetSelectionSearchSchema = z.object({
  page: z.number().optional().default(1),
  pageSize: z.number().optional().default(100),
  freeText: z.string().optional(),
  /**
   * アラートカテゴリ（単一選択）
   * カテゴリごとに独立してサーバーサイドPaginationするため単一値
   * 未指定の場合は operationType に応じたデフォルトカテゴリ
   */
  alertCategory: z.enum(ALERT_CATEGORIES).optional(),
  /**
   * 組織改編プラン IDの配列（"異動予定者" カテゴリ用）
   * 未指定・空配列のときは dateRange に該当する全プランを横断集約
   */
  restructuringPlanIdIn: z.array(z.string()).optional(),
  workerTypeIdIn: z.array(z.string()).optional(),
  placeIdIn: z.array(z.string()).optional(),
  /**
   * Correct Role (SoftwareAssignmentPolicy 由来) で絞り込む role ID 配列。
   * Manual モード (UnassignedBulkAppUserTargetSelection) でのみ使用。
   * `includeUnassignedRole` と排他で、UI 側で同時に有効化されない想定。
   */
  correctRoleIdIn: z.array(z.string()).optional(),
  /**
   * true で Correct Role が未設定 (Policy 行なし or role null) の person のみ返す。
   * `correctRoleIdIn` と排他。
   */
  includeUnassignedRole: z.boolean().optional(),
  /** BasicWorker カテゴリ（デバイス手配用）。未指定時は REGULAR_ALL */
  category: z.string().optional(),
  /**
   * 同一パターンで起動済みのメンバーを候補から除外する絞り込み（セルフレポート）。
   * 未指定時はパッケージごとの既定値にフォールバックする。
   */
  excludeStartedTargets: z.enum(STARTED_TARGETS_FILTER_OPTIONS).optional(),
  sortColumn: z.string().optional(),
  sortOrder: z.nativeEnum(OrderDirection).optional()
});
export type ITOperationCoreTargetSelectionSearch = z.infer<typeof itOperationCoreTargetSelectionSearchSchema>;

/**
 * HRF（人事系）の絞り込みが有効なカテゴリかどうかを判定
 *
 * - Provisioning 系: 人に対するアカウント発行 → HRF属性での絞り込みが有効
 * - deprovisioning-alumni: 離籍者 → HRF属性での絞り込みが有効
 * - deprovisioning-violated / deprovisioning-scheduled: ITF関連 → HRF属性での絞り込みは対象外
 */
export function isHrFilterableCategory(category: AlertCategory): boolean {
  return category === 'provisioning-scheduled' || category === 'deprovisioning-alumni' || category === 'restructuring';
}

/**
 * operationType に応じたデフォルトのアラートカテゴリを取得
 */
export function getDefaultAlertCategory(operationType: 'provisioning' | 'deprovisioning'): AlertCategory {
  return operationType === 'provisioning' ? 'provisioning-required' : 'deprovisioning-violated';
}